<template>
  <div class="order">
    <not-found v-if="hasError" />
    <template v-else>
      <b-container v-if="!isCompleted" fluid class="order-detail">
        <b-row
          v-show="orderRestricted"
          class="text-center"
          style="padding-top: 16px; display: flex"
        >
          <b-col>
            <div class="orderRestricted">{{ $t('error.limitErrorDescription') }}</div>
          </b-col>
        </b-row>
        <app-loader v-if="!isLoaded" />
        <b-row v-else class="order-image">
          <b-col cols="1"></b-col>
          <b-col cols="10">
            <div class="image-wrap">
              <img
                v-if="isJp && isBikkuraponIconDisplay && isBikkurapon(unitType)"
                class="bikkurapon-icon"
                src="@/assets/images/bikkurapon_icon.png"
                alt=""
              />
              <img
                v-if="isTw && isBikkuraponIconDisplay && isBikkurapon(unitType)"
                class="bikkurapon-icon"
                src="@/assets/images/bikkurapon_icon_tw.png"
                alt=""
              />
              <img
                v-if="isUs && isBikkuraponIconDisplay && isBikkurapon(unitType)"
                class="bikkurapon-icon"
                src="@/assets/images/bikkurapon_icon_us.png"
                alt=""
              />
              <img
                v-if="isCn && isBikkuraponIconDisplay && isBikkurapon(unitType)"
                class="bikkurapon-icon"
                src="@/assets/images/bikkurapon_icon_cn.png"
                alt=""
              />
              <img :src="itemImage" style="border-radius: 5px" alt="" @error="replaceByDefault" />
              <div v-if="isPriceDisplay" class="price">
                <div v-if="isUs" class="price is-us">
                  {{ $t('common.dollar', { price: (price / 100).toFixed(2) }) }}
                </div>
                <div v-if="!isUs">{{ price | setComma }}</div>
                <div v-if="isJp" class="unit">{{ $t('common.yen') }}</div>
                <div v-if="isTw" class="unit">{{ $t('common.twGen') }}</div>
                <div v-if="isCn" class="unit">{{ $t('common.cnGen') }}</div>
              </div>
            </div>
          </b-col>
          <b-col cols="1"></b-col>
        </b-row>
        <b-row v-if="isLoaded" class="order-titleArea">
          <b-col>
            <div class="order-titleArea-title">
              {{ itemName }}
              <p v-if="calorie" class="order-titleArea-calorie">{{ calorie }}</p>
            </div>
          </b-col>
        </b-row>
        <b-row
          v-for="(topping, key) in toppings"
          :id="`topping-${key}`"
          :key="key"
          class="order-buttonArea"
          :tag="'form'"
        >
          <b-col
            v-for="(option, optionKey) in topping"
            :key="optionKey"
            :cols="12 / topping.length"
            class="mr5"
          >
            <input
              :id="`topping-${option.toppingCode}`"
              v-model="picked[`${option.toppingGroup}`]"
              type="radio"
              :disabled="orderInProgress"
              :value="option"
              :name="`${option.toppingGroup}`"
              :checked="option.defaultSelect === 0"
              style="display: none"
            />
            <label class="order-buttonArea-button" :for="`topping-${option.toppingCode}`">
              {{ option.name }}
            </label>
          </b-col>
        </b-row>
        <b-row v-if="isLoaded" class="order-buttonArea">
          <b-col v-for="(_, index) in Array(maxSameTimeOrderCount)" :key="index" cols="3">
            <div
              :class="{ active: unitCount === index + 1 }"
              class="order-buttonArea-button"
              @click="changeUnitCount(index + 1)"
            >
              {{ index + 1 }}<template v-if="!isUs && !isJp">{{ unit }}</template>
            </div>
          </b-col>
        </b-row>
        <div v-if="isLoaded && isUs" class="raw-attention-text">
          <p>
            *These food items are served raw or undercooked ingredients. Consuming raw or
            undercooked meats, poultry, seafood, shellfish or eggs may increase your risk of
            foodborne illness, especially if you have certain medical conditions.
          </p>
          <p>
            2,000 calories a day is used for general nutrition advice, but calorie needs vary.
            Additional nutrition information available upon request.
          </p>
          <p>
            Wheat, soy, peanuts, and other food allergens are present at our restaurant. Due to the
            design of our operations and shared cooking equipment, we cannot guarantee that any of
            our dishes are allergen free. Please visit our website at kurasushi.com for allergy
            information.
          </p>
          <p>Actual products may vary.</p>
        </div>
        <div class="cautionary-notes">
          <span class="cautionaryNote"> {{ cautionaryNote }} </span>
        </div>
      </b-container>
      <b-container v-else class="order-completed">
        <b-row style="background-color: #fff; padding-bottom: 55px">
          <b-col>
            <img
              class="order-completed-closeIcon"
              src="@/assets/images/icn_close.svg"
              @click="closeCompletedView"
            />
            <div class="order-completed-thanks">{{ $t('order.thankYouForYourOrder') }}</div>
            <ul>
              <li>{{ itemName }}</li>
              <li>
                <span v-for="(item, key) in picked" :key="key"> {{ item.name }}</span>
                <span>
                  {{ unitCount }}<template v-if="!isJp && !isUs">{{ unit }}</template>
                </span>
              </li>
            </ul>
            <div>{{ $t('order.theOrderHasBeenReceived') }}</div>
          </b-col>
        </b-row>
        <b-row v-show="isRecommended" class="recommendArea">
          <b-row class="recommendArea-namako">
            <img src="@/assets/images/bg_namako.svg" style="width: 100%; margin-top: -2px" alt="" />
          </b-row>
          <b-row class="recommendArea-title">
            <b-col>{{ $t('order.arrangeOfSpecialAndPopularMenu') }}</b-col>
          </b-row>
          <two-column-menu
            class="recommendArea-menu"
            :items="recommendedItemList"
            @onItemTappedWith="onItemTappedWith"
          />
        </b-row>
      </b-container>

      <b-container v-if="!isCompleted" class="footer" fluid>
        <b-row no-gutters>
          <b-col class="col" cols="5">
            <b-button class="footer-button footer-back" @click="back()">
              {{ $t('common.return') }}
            </b-button>
          </b-col>
          <b-col class="col" cols="7">
            <b-button
              :disabled="!isLoaded || orderRestricted || orderInProgress"
              class="footer-button footer-order"
              @click="doOrder"
            >
              {{ $t('common.order') }}
            </b-button>
          </b-col>
        </b-row>
      </b-container>

      <b-modal
        ref="result2-modal"
        centered
        hide-header
        hide-footer
        hide-header-close
        @hidden="closeModal()"
      >
        <p class="my-4">
          <span v-if="isPreOrder">{{ $t('preOrder.preOrderLimitOver') }}</span>
          <span v-else>{{ $t('error.limitErrorDescription') }}</span>
        </p>
        <div block class="closeButton" @click="closeModal('result2-modal')">
          {{ $t('common.close') }}
        </div>
      </b-modal>
      <b-modal
        ref="result3-modal"
        centered
        hide-header
        hide-footer
        hide-header-close
        @hidden="closeModal()"
      >
        <p class="my-4">
          {{ $t('error.orderStopErrorDescription') }}
        </p>
        <div block class="closeButton" @click="closeModal('result3-modal')">
          {{ $t('common.close') }}
        </div>
      </b-modal>
      <b-modal
        ref="result4-modal"
        centered
        hide-header
        hide-footer
        hide-header-close
        @hidden="closeModal('result4-modal')"
      >
        <p class="my-4">
          {{ $t('error.stockoutErrorDescription') }}
        </p>
        <div block class="closeButton" @click="closeModal('result4-modal')">
          {{ $t('common.close') }}
        </div>
      </b-modal>
      <b-modal
        ref="error-modal"
        centered
        hide-header
        hide-footer
        hide-header-close
        @hidden="closeModal('error-modal')"
      >
        <p class="my-4 title">
          {{ $t('error.failedToProcess') }}
        </p>
        <p class="my-4">
          {{ $t('error.orderError') }}
        </p>
        <div block class="closeButton" @click="closeModal('error-modal')">
          {{ $t('common.close') }}
        </div>
      </b-modal>
    </template>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'

import config from '@/config/config'
import * as api from '@/api'
import { getServerDateTime, getLocalDateTime } from '@/utils/time'

import TwoColumnMenu from '@/components/menuList/TwoColumnMenu'
import AppLoader from '@/components/loader/AppLoader'
import NotFound from '@/components/error/NotFound'
import noImage from '@/assets/images/noimage.png'

export default {
  name: 'Order',
  components: {
    TwoColumnMenu,
    AppLoader,
    NotFound,
  },
  filters: {
    setComma: function (value) {
      return value.toLocaleString()
    },
  },
  data() {
    return {
      unitCount: 1,
      isCompleted: false,
      orderContents: [],
      itemName: '',
      cautionaryNote: '',
      price: 0,
      calorie: null,
      toppingInfo: [],
      toppings: [],
      picked: {},
      itemImage: '',
      toppingGroups: [],
      isLoaded: false,
      itemId: '',
      isRecommended: false,
      recommendedItemList: [],
      unitType: 0,
      timeoutHandle: null,
      orderInProgress: false, // 注文処理中か 注文ボタンの表示制御に使用
      hasError: false,
      noImage: noImage,
    }
  },
  computed: {
    unit() {
      return config.unit[this.language][this.unitType]
    },
    ...mapGetters({
      activeCategoryId: 'menu/getActiveCategoryId',
      language: 'setting/getLanguage',
      shopId: 'seat/getShopId',
      seatNo: 'seat/getValidSeatNo',
      isPreOrder: 'seat/getIsPreOrder',
      orderRestriction: 'seat/getOrderRestriction',
      priceDisplayEnabled: 'seat/getPriceDisplayEnabled',
      bikkuraponIconDisplay: 'seat/getIsBikkuraponIconDisplay',
      preOrderPriceDisplayEnabled: 'shopDetail/getPriceDisplayEnabled',
      preOrderBikkuraponIconDisplay: 'shopDetail/getIsBikkuraponIconDisplay',
    }),
    orderRestricted() {
      return this.orderRestriction === 3
    },
    isBikkuraponIconDisplay() {
      return this.isPreOrder ? this.preOrderBikkuraponIconDisplay : this.bikkuraponIconDisplay
    },
    isBikkurapon() {
      return (unit) => {
        return unit !== 0
      }
    },
    defaultLanguage() {
      return process.env.VUE_APP_LANG
    },
    isJp() {
      return this.defaultLanguage === 'ja'
    },
    isTw() {
      return this.defaultLanguage === 'zh-tw'
    },
    isUs() {
      return this.defaultLanguage === 'us'
    },
    isCn() {
      return this.defaultLanguage === 'zh-cn'
    },
    maxSameTimeOrderCount() {
      return config.maxSameTimeOrderCount
    },
    isPriceDisplay() {
      return this.isPreOrder
        ? this.preOrderPriceDisplayEnabled === 1
        : this.priceDisplayEnabled === 1
    },
  },
  watch: {
    itemId(itemId) {
      if (!itemId) {
        return
      }
      this.fetchMenuDetail(this.shopId, this.seatNo, itemId)
    },
    toppingInfo(toppings) {
      this.toppings = []
      // トッピンググループの重複を消す
      let toppingGroups = Array.from(new Set(toppings.map((topping) => topping.toppingGroup)))
      toppingGroups.forEach((item) => {
        this.picked[`${item}`] = null
      })
      this.toppingGroups = toppingGroups
      // トッピンググループごとにまとめる
      toppingGroups.forEach((toppingGroup) => {
        const toppingList = toppings.filter((topping) => {
          if (topping.defaultSelect === 0) {
            this.picked[`${toppingGroup}`] = topping
          }
          return topping.toppingGroup === toppingGroup
        })
        this.toppings.push(toppingList)
      })
    },
    language() {
      this.$emit('updateHead')
      this.itemId = this.$route.params.id
      this.fetchMenuDetail(this.shopId, this.seatNo, this.itemId)
    },
    // 完了後、5秒たったら商品一覧画面に戻る
    isCompleted(completed) {
      if (!completed) {
        return
      }
      this.itemId = this.$route.params.id
      api
        .fetchOrderRecommend(this.shopId, this.itemId)
        .then((res) => {
          if (res.data.itemList.length > 0) {
            this.isRecommended = true
            this.recommendedItemList = res.data.itemList
          } else {
            this.isRecommended = false
            this.recommendedItemList = []
          }
        })
        .catch(() => {
          this.isRecommended = false
          this.recommendedItemList = []
        })
      this.timeoutHandle = setTimeout(() => {
        this.$router.push({ name: 'shopMenu' })
      }, 5000)
    },
  },
  mounted() {
    this.itemId = this.$route.params.id
  },
  destroyed() {
    clearTimeout(this.timeoutHandle)
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === 'search' && from.name === 'searchOrder') {
      this.setShowSearchResult(true)
    } else {
      this.setShowSearchResult(false)
    }
    this.hasError = false
    next()
  },
  methods: {
    ...mapActions({
      setShowSearchResult: 'search/setShowSearchResult',
      setError: 'error/setError',
      setShowingSimpleErrorModal: 'modal/setShowingSimpleErrorModal',
    }),
    changeUnitCount(number) {
      if (this.orderInProgress) return // 注文途中であれば変更しない
      this.unitCount = number
    },
    doOrder() {
      if (this.orderInProgress === true) return
      this.orderInProgress = true
      const toppings = Object.keys(this.picked).map((key) => {
        return this.picked[key].toppingCode
      })
      const params = {
        itemCode: this.itemId,
        toppings: toppings,
        quantity: this.unitCount,
        influxType: this.getInfluxType(),
        influxSource: this.getInfluxSource(),
        orderedAt: getLocalDateTime(new Date()),
        requestedAt: getServerDateTime(new Date()),
        reserve: [],
      }
      api
        .orderMenu(this.shopId, this.seatNo, params)
        .then((res) => {
          if (res.data.result === 1) {
            this.$store.dispatch('scheduledRecommend/setLastOrderTime', new Date().getTime())
            this.isCompleted = true
            this.orderInProgress = false
          } else if (res.data.result === 2) {
            this.$refs['result2-modal'].show()
          } else if (res.data.result === 3) {
            this.$refs['result3-modal'].show()
          } else if (res.data.result === 4) {
            this.$refs['result4-modal'].show()
          }
        })
        .catch(() => {
          this.$refs['error-modal'].show()
          this.orderInProgress = false
        })
    },
    fetchMenuDetail(shopId, seatNo, itemId) {
      return api
        .fetchMenuDetail(shopId, seatNo, itemId)
        .then((res) => {
          this.toppingInfo = res.data.toppings
          this.itemName = res.data.name
          this.cautionaryNote = res.data.cautionaryNote
          this.itemImage = res.data.itemImage
          this.unitType = res.data.unit
          this.price = res.data.price
          this.calorie =
            res.data.calorie === null
              ? null
              : res.data.calorie.toLocaleString() + this.$t('common.calorieUnit')
          this.isLoaded = true
          this.$emit('updateHead')
        })
        .catch((err) => {
          if (err.response) {
            this.hasError = true
          } else {
            this.setShowingSimpleErrorModal(true)
          }
        })
    },
    getInfluxType() {
      return this.$route.meta.influxType
    },
    getInfluxSource() {
      switch (this.$route.name) {
        case 'order':
          return this.activeCategoryId
        case 'adOrder':
          return Number(this.$route.params.adId)
        case 'recommendOrder':
          return Number(this.$route.params.fromId)
        case 'historyOrder':
          return Number(this.$route.params.historyId)
        case 'searchOrder':
          return Number(this.$route.params.id)
        default:
          return 1
      }
    },
    closeCompletedView() {
      this.$router.push({ name: 'shopMenu' })
    },
    onItemTappedWith(itemCode) {
      if (this.timeoutHandle) {
        clearTimeout(this.timeoutHandle)
      }
      this.viewReset()
      this.$router.push(`/order/${itemCode}`)
      this.itemId = itemCode
    },
    viewReset() {
      this.unitCount = 1
      this.isCompleted = false
      this.orderContents = []
      this.itemName = ''
      this.cautionaryNote = ''
      this.price = 0
      this.toppingInfo = []
      this.toppings = []
      this.picked = {}
      this.itemImage = ''
      this.toppingGroups = []
      this.isLoaded = false
      // this.itemId = ''
      this.isRecommended = false
      this.recommendedItemList = []
      this.unitType = 0
    },
    closeModal(modal) {
      // 欠品モーダルならメニューページにリダイレクトさせる
      if (modal === 'result4-modal') {
        this.$refs[modal].hide()
        this.orderInProgress = false
        this.$router.go(-1)
        return
      }
      if (modal) {
        this.$refs[modal].hide()
      }
      this.orderInProgress = false
    },
    back() {
      this.$router.go(-1)
    },
    replaceByDefault(e) {
      e.target.src = noImage
    },
  },
  head: {
    title() {
      const itemName = this.itemName
      const subTitle = this.$t(`pageTitle.${this.$route.name}`)
      return {
        inner: `${itemName ? itemName + ' | ' : ''}${subTitle ? subTitle : ''}`,
        complement: this.$t('siteId'),
      }
    },
  },
}
</script>
<style lang="scss" scoped>
input[type='radio']:checked ~ label {
  background-color: #a58149;
  border-bottom: 2px solid #ab8a56;
  color: #fff;
}
.order {
  color: #222;
  &-image {
    padding: 15px 0;
    text-align: center;
  }
  &-titleArea {
    &-title {
      text-align: left;
      color: #222;
      font-size: 18px;
      font-weight: bold;
      box-sizing: border-box;
      margin-top: 10px;
      border-bottom: 1px solid #ebebeb;
      padding-bottom: 15px;
    }
    &-calorie {
      margin: 0;
      font-size: 16px;
      font-weight: normal;
    }
  }
  &-buttonArea {
    margin: 10px 0px;
    padding-bottom: 15px;
    border-bottom: 1px solid #ebebeb;
    > div {
      padding-left: 0px;
      padding-right: 0px;
      display: flex;
      -webkit-box-pack: justify;
      -ms-flex-pack: justify;
      justify-content: space-between;
    }
    &-button {
      display: inline-block;
      margin: 0 auto;
      width: calc(100% - 10px);
      display: flex;
      justify-content: center;
      align-items: center;
      color: #222;
      font-weight: bold;
      background-color: #e5e5e5;
      border-bottom: 2px solid #d6d6d6;
      border-radius: 5px;
      padding: 15px;
      box-sizing: border-box;
      font-size: 1rem;
      position: relative;
      height: 55px;
      box-sizing: border-box;
    }
    .active {
      background-color: #a58149;
      border-bottom: 2px solid #ab8a56;
      color: #fff;
      box-sizing: border-box;
    }
  }
  .recommendArea {
    background-color: #fff;
    &-namako {
      height: 10px;
      width: 100%;
      background-repeat: repeat-x;
      background-color: #222;
      border: none;
      margin-left: 0px;
      margin-right: 0px;
    }
    &-title {
      border: none;
      width: 100%;
      background-color: #222;
      margin: 0;
      margin-top: -1px; // 間に線が見えてしまうので…。
      font-size: 18px;
      line-height: 3em;
      color: #fff;
    }
    &-menu {
      padding: 20px 12px;
    }
  }
  .orderRestricted {
    background-color: #fcf8e3;
    color: #66526a;
    font-size: 0.9rem;
    padding: 15px;
    box-sizing: border-box;
    font-weight: bold;
    border: solid #ccc 1px;
    border-radius: 5px;
    line-height: 1.7;
  }
  &-detail {
    padding-bottom: 20px;
  }
  &-completed {
    background-color: #fff;
    top: 55px;
    min-height: calc(100% - 55px);
    width: 100%;
    padding-bottom: 20px;
    &-thanks {
      color: #222;
      font-size: 18px;
      font-weight: bold;
    }
    ul {
      list-style: none;
      padding: 0;
      padding-top: 1rem;
    }
    li {
      color: #d21b24;
      font-weight: bold;
    }
    &-closeIcon {
      display: inherit;
      clear: both;
      width: 35px;
      height: 35px;
      margin-top: 15px;
    }
  }
}
.image-wrap {
  position: relative;
  max-width: 400px;
  margin: 0 auto;
  .bikkurapon-icon {
    position: absolute;
    top: 4px;
    left: 3px;
    z-index: 1;
    width: 27%;
  }
}
.price {
  display: flex;
  align-items: baseline;
  position: absolute;
  bottom: 0;
  left: 10px;
  z-index: 1;
  color: #fff;
  font-size: clamp(34px, 9vw, 50px);
  font-weight: bold;
  text-shadow: 0px 0px 2px black, 0px 0px 2px black, 0px 0px 3px black;
  font-family: 'Zen Antique Soft', serif;

  .unit {
    margin-left: 2px;
    font-size: clamp(18px, 5.3vw, 26px);
    font-family: 'Zen Antique Soft', serif;
  }
  &.is-us {
    left: 0;
  }
}
.order-detail {
  padding-bottom: 70px;
}
.raw-attention-text {
  margin-top: 20px;
  text-align: left;
  font-size: 12px;
  color: #888;
  p {
    margin-bottom: 10px;
  }
}
.cautionary-notes {
  margin-top: 20px;
  text-align: left;
  font-size: 14px;
  color: red;
  .cautionaryNote {
    white-space: pre-line;
  }
}
.footer {
  position: fixed;
  bottom: 0;
  left: 0;
  padding: 10px 5px;
  border-top: solid #f3f3f3 1px;
  background: #fff;
  text-align: center;
  z-index: 40;

  .col {
    padding: 0 5px;
  }

  &-button {
    display: block;
    width: 100%;
    padding: 15px;
    border-radius: 5px;
    font-size: 18px;
    font-weight: bold;
    color: #fff;
    box-sizing: border-box;
  }
  &-back {
    background-color: #656565;
    border-bottom: 2px solid #535353;
    &:active {
      border-top: 2px solid #fff;
      border-bottom: 0px solid #535353;
    }
  }
  &-order {
    background-color: #d21b24;
    border: none;
    border-bottom: 2px solid #ad1219;
    &:active {
      border-top: 2px solid #fff;
      border-bottom: 0px solid #ad1219;
    }
    &:disabled {
      background-color: #e5e5e5;
      border-top: none;
      border-right: none;
      border-bottom: 2px solid #d6d6d6;
      border-left: none;
      &:active {
        border-bottom: 2px solid #d6d6d6 !important;
        margin-top: 0px !important;
        margin-bottom: 0px !important;
      }
    }
  }
}
.my-4 {
  white-space: pre-line;

  &.title {
    margin-top: 0.5rem !important;
    text-align: center;
    font-weight: 700;
  }
}
</style>
